import React, { useState } from "react";
import { Badge, Tabs, Tab, Card, Row, Col } from "react-bootstrap";
import {
    FaExchangeAlt,
    FaInfoCircle, FaFileInvoiceDollar, FaMoneyBillWave, FaUser, FaBuilding, FaCalendarAlt, FaCheckCircle, FaReceipt, FaTimes,
    FaUserTie
} from "react-icons/fa";
import BrandTable from "../core/components/BrandTable/BrandTable";
import { formatCurrency } from "../../utility";

const PaymentDetailsCard = ({ data, onClose }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const payment = data;

    if (!payment) return null;

    // Columns for Purchase Orders reference table
    const purchaseOrderColumns = [
        {
            key: 'purchase_id',
            label: 'Purchase ID',
            render: (val) => <span className="font-monospace small">{val?._id || val}</span>
        },
        {
            key: 'referenceNumber',
            label: 'Ref No',
            render: (val, row) => row.purchase_id?.referenceNumber || "N/A"
        },
        {
            key: 'paidAmount',
            label: 'Amount Paid',
            align: 'right',
            render: (val) => <span className="fw-bold text-success">{formatCurrency(val)}</span>
        }
    ];

    // Extract related purchase orders data if available
    const purchaseOrdersData = payment.references?.purchase_orders || [];

    return (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-white animate__animated animate__fadeIn" style={{ zIndex: 9999, overflow: "auto", display: 'flex', flexDirection: 'column' }}>
            <style>
                {`
          .details-label { font-size: 0.8rem; color: #8592a3; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; margin-bottom: 4px; }
          .details-value { font-size: 1.1rem; color: #566a7f; font-weight: 500; }
          .spec-card { background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 12px; padding: 16px; transition: all 0.2s; }
          .spec-card:hover { border-color: #696cff; background: #fff; box-shadow: 0 4px 12px rgba(105, 108, 255, 0.08); }
          .intel-tabs .nav-link {
              padding: 1.25rem 2rem;
              font-weight: 600;
              border: none;
              color: #697a8d;
              display: flex;
              align-items: center;
              gap: 10px;
              transition: all 0.3s;
          }
          .intel-tabs .nav-link.active {
              color: #696cff!important;
              background: #fff!important;
              border-bottom: 3px solid #696cff!important;
              box-shadow: 0 4px 10px rgba(105, 108, 255, 0.1);
          }
          .intel-tabs .nav-link:hover:not(.active) { background: #fdfdff; color: #696cff; }
        `}
            </style>

            {/* Header Section */}
            <div className="container-fluid p-0">
                <div className="row g-0 border-bottom bg-white sticky-top shadow-sm px-4 py-3" style={{ zIndex: 1000 }}>
                    <div className="col-12 d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-3">
                            <h4 className="mb-0 fw-bold">Payment Details</h4>
                            <Badge bg="primary" className="shadow-sm font-monospace">ID: {payment._id}</Badge>
                            <span className="text-muted small">|</span>
                            <span className="text-muted small fw-bold text-uppercase">{payment.transaction_type}</span>
                        </div>
                        <div className="d-flex gap-3 align-items-center">
                            <div className="text-end">
                                <div className="text-muted x-small fw-bold text-uppercase">Total Amount</div>
                                <div className="fw-bold text-success fs-4">{formatCurrency(payment.amount)}</div>
                            </div>
                            {onClose && (
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="d-flex align-items-center justify-content-center border rounded-circle btn btn-light btn-sm"
                                    style={{ width: "40px", height: "40px" }}
                                    title="Close"
                                >
                                    <FaTimes size={18} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="intel-tabs px-4 bg-light border-bottom sticky-top"
                    style={{ top: '56px', zIndex: 999 }}
                >
                    <Tab eventKey="overview" title={<><FaInfoCircle /> Overview</>}>
                        <div className="row g-0">
                            {/* Left Panel: Visual/Summary */}
                            <div className="col-12 col-xl-4 bg-light border-end p-5">
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="d-flex flex-column align-items-center justify-content-center text-center p-5">
                                        <div className="mb-4">
                                            <div className="avatar avatar-xl rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center mx-auto" style={{ width: 100, height: 100 }}>
                                                <FaMoneyBillWave size={40} className="text-success" />
                                            </div>
                                        </div>
                                        <h3 className="fw-bold text-dark mb-1">{formatCurrency(payment.amount)}</h3>
                                        <p className="text-muted mb-4">{payment.payment_method || "Unknown Method"}</p>

                                        <div className="w-100 border-top pt-4 mt-2">
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted">Date</span>
                                                <span className="fw-bold">{new Date(payment.transaction_date || payment.createdAt).toLocaleDateString()}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted">Time</span>
                                                <span className="fw-bold">{new Date(payment.transaction_date || payment.createdAt).toLocaleTimeString()}</span>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>

                            {/* Right Panel: Detailed Info */}
                            <div className="col-12 col-xl-8 p-4 p-md-5 bg-white">
                                <div className="mb-5">
                                    <h5 className="fw-bold mb-4 d-flex align-items-center text-dark">
                                        <div className="p-2 bg-primary bg-opacity-10 rounded-3 me-2">
                                            <FaExchangeAlt className="text-primary" size={16} />
                                        </div>
                                        Account Routing & Ledger
                                    </h5>
                                    <div className="row g-4">
                                        <Col md={6}>
                                            <div className="spec-card h-100 border-start border-4 border-primary">
                                                <div className="d-flex align-items-center mb-2">
                                                    <FaBuilding className="text-muted me-2" /> <span className="details-label mb-0">Disbursed From</span>
                                                </div>
                                                <div className="details-value fw-bold text-dark">{payment.from_account_id?.account_name || "N/A"}</div>
                                                <div className="x-small text-muted text-uppercase fw-bold">{payment.from_account_id?.account_type || "Account"} Pool</div>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="spec-card h-100 border-start border-4 border-success">
                                                <div className="d-flex align-items-center mb-2">
                                                    <FaBuilding className="text-muted me-2" /> <span className="details-label mb-0">Received Into</span>
                                                </div>
                                                <div className="details-value fw-bold text-dark">{payment.to_account_id?.account_name || "N/A"}</div>
                                                <div className="x-small text-muted text-uppercase fw-bold">{payment.to_account_id?.account_type || "Account"} Ledger</div>
                                            </div>
                                        </Col>
                                    </div>
                                </div>

                                <div className="mb-5">
                                    <h5 className="fw-bold mb-4 d-flex align-items-center text-dark">
                                        <div className="p-2 bg-info bg-opacity-10 rounded-3 me-2">
                                            <FaUser className="text-info" size={16} />
                                        </div>
                                        Counterparty Information
                                    </h5>
                                    <div className="row g-4">
                                        {payment.references?.supplier && (
                                            <Col md={12}>
                                                <div className="spec-card bg-light border-0 shadow-sm d-flex align-items-center gap-3">
                                                    <div className="avatar avatar-md rounded-circle bg-white shadow-sm d-flex align-items-center justify-content-center">
                                                        <FaBuilding className="text-primary" />
                                                    </div>
                                                    <div>
                                                        <div className="details-label mb-0">Supplier Name</div>
                                                        <div className="details-value fw-bold">{payment.references.supplier.supplier_name}</div>
                                                        <small className="text-muted font-monospace">{payment.references.supplier.supplier_id}</small>
                                                    </div>
                                                </div>
                                            </Col>
                                        )}
                                        {payment.references?.customer && (
                                            <Col md={12}>
                                                <div className="spec-card bg-light border-0 shadow-sm d-flex align-items-center gap-3">
                                                    <div className="avatar avatar-md rounded-circle bg-white shadow-sm d-flex align-items-center justify-content-center">
                                                        <FaUser className="text-info" />
                                                    </div>
                                                    <div>
                                                        <div className="details-label mb-0">Customer / Client</div>
                                                        <div className="details-value fw-bold">{payment.references.customer.customer_name}</div>
                                                    </div>
                                                </div>
                                            </Col>
                                        )}
                                        {payment.references?.employee && (
                                            <Col md={12}>
                                                <div className="spec-card bg-light border-0 shadow-sm d-flex align-items-center gap-3">
                                                    <div className="avatar avatar-md rounded-circle bg-white shadow-sm d-flex align-items-center justify-content-center">
                                                        <FaUserTie className="text-warning" />
                                                    </div>
                                                    <div>
                                                        <div className="details-label mb-0">Internal Staff</div>
                                                        <div className="details-value fw-bold">{payment.references.employee.first_name} {payment.references.employee.last_name}</div>
                                                    </div>
                                                </div>
                                            </Col>
                                        )}
                                    </div>
                                </div>

                                {/* Description Section */}
                                <div className="mt-4">
                                    <h5 className="fw-bold mb-3 d-flex align-items-center text-dark">
                                        <div className="p-2 bg-secondary bg-opacity-10 rounded-3 me-2">
                                            <FaReceipt className="text-secondary" size={16} />
                                        </div>
                                        Transaction Narrative
                                    </h5>
                                    <div className="p-4 bg-light rounded-4 border-dashed border-2">
                                        <p className="text-muted mb-0 lh-base italic" style={{ fontSize: '1.05rem', whiteSpace: 'pre-line', fontStyle: 'italic' }}>
                                            "{payment.description || "No specific narrative provided for this financial movement."}"
                                        </p>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </Tab>

                    <Tab eventKey="references" title={<><FaFileInvoiceDollar /> References & Documents</>}>
                        <div className="p-4 p-md-5">
                            {purchaseOrdersData.length > 0 ? (
                                <BrandTable
                                    title="Related Purchase Orders"
                                    data={purchaseOrdersData}
                                    columns={purchaseOrderColumns}
                                    emptyMessage="No linked purchase orders."
                                    stripe={true}
                                />
                            ) : (
                                <div className="text-center p-5 text-muted">
                                    <FaFileInvoiceDollar size={40} className="mb-3 opacity-50" />
                                    <h5>No linked references found</h5>
                                    <p>This payment is not directly linked to specific purchase orders or invoices.</p>
                                </div>
                            )}
                        </div>
                    </Tab>

                </Tabs>

            </div>
        </div>
    );
};

// Start of FA Icon imports fix if needed (FaExchangeAlt used but not imported in top block, let me check)
// FaExchangeAlt IS imported in the top block from react-icons/fa. Looks good.
// Adding FaExchangeAlt to imports just to be safe if I missed it in the string block above.
// Wait, I see I put it in the imports. 

export default PaymentDetailsCard;
